import React from 'react'
import styled from '@emotion/styled'
import { ExpandMore } from '@emotion-icons/material/ExpandMore'
import { ExpandLess } from '@emotion-icons/material/ExpandLess'
import { Typography } from '@mui/material'
import { Colors, Shapes } from 'src/components/theme'
import {
  InsightCardHeaderLeft,
  InsightCardHeaderLeftExtra,
  InsightCardHeaderRight,
  InsightCardHeaderTitle,
  InsightCardWrapper,
  InsightCardWrapperProps,
} from 'src/components/insights/common/cards/card'
import { INSIGHT_WIDGET_PRINTABLE_CLASS } from 'src/atoms/report-insight'

type InsightCardCollapsibleProps = {
  id?: string
  title?: string
  left?: React.ReactNode
  right?: React.ReactNode
  children?: React.ReactNode
  open: boolean
  onToggle: (open: boolean) => void
  headerInteractive?: boolean
}

type InsightCardCollapsibleContentProps = {
  open?: boolean
}

export const InsightCardCollapsibleWrapper = styled.div<InsightCardWrapperProps>`
  position: relative;
  width: 100%;
  background-color: ${Colors.bgWhite};
  margin: ${Shapes.spacing[4]}px 0;
  border: 1px solid ${Colors.border};
  padding: 0;
  padding: ${(props: InsightCardWrapperProps) =>
    props.padding !== undefined ? `${props.padding}px` : `${Shapes.spacing[4]}px`};
  border-radius: ${(props: InsightCardWrapperProps) =>
    props.isFooterOpen ? `${Shapes.borderRadius}px ${Shapes.borderRadius}px 0 0` : Shapes.borderRadius}px;
  overflow: hidden;
`

const InsightCardCollapsibleHeader = styled.div<{ headerInteractive?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Shapes.spacing[2]}px;
  cursor: ${(props) => (props.headerInteractive ? 'initial' : 'pointer')};
  margin-bottom: 0;
`

const InsightCardCollapsibleHeaderRightExtra = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${Shapes.spacing[4]}px;

  & > * {
    margin-left: ${Shapes.spacing[3]}px;
  }
`

const InsightCardCollapsibleContent = styled.div<InsightCardCollapsibleContentProps>`
  position: relative;
  height: ${(props) => (props.open ? 'auto' : '0px')};
  overflow: ${(props) => (props.open ? 'initial' : 'hidden')};
`

export const InsightCardCollapsibleContentInner = styled.div<{ marginTop?: number }>`
  padding: ${Shapes.spacing[2]}px ${Shapes.spacing[2] + 59}px ${Shapes.spacing[2]}px ${Shapes.spacing[2]}px;
  ${(props) => (props.marginTop ? `margin-top: ${Shapes.spacing[props.marginTop]}px` : '')}
`

const InsightCardCollapsibleToggleWrapper = styled.div`
  width: 15px;
  height: 15px;
`

export const InsightCardCollapsible: React.FC<InsightCardCollapsibleProps> = (props) => {
  const { id, title, left, right, children, open, onToggle, headerInteractive = true } = props

  const onHeaderClick = () => {
    if (headerInteractive) {
      return
    }
    onToggle(!open)
  }

  return (
    <div id={id} className={`print-no-margin ${INSIGHT_WIDGET_PRINTABLE_CLASS}`}>
      <InsightCardWrapper>
        <InsightCardCollapsibleHeader onClick={onHeaderClick} headerInteractive={headerInteractive}>
          <InsightCardHeaderLeft>
            {title && (
              <InsightCardHeaderTitle>
                <Typography variant="title1" color={Colors.textBlack}>
                  {title}
                </Typography>
              </InsightCardHeaderTitle>
            )}
            <InsightCardHeaderLeftExtra>{left}</InsightCardHeaderLeftExtra>
          </InsightCardHeaderLeft>
          <InsightCardHeaderRight>
            <InsightCardCollapsibleHeaderRightExtra>{right}</InsightCardCollapsibleHeaderRightExtra>
            <InsightCardCollapsibleToggleWrapper onClick={() => onToggle(!open)} className="noprint">
              {open ? <ExpandLess size={15} /> : <ExpandMore size={15} />}
            </InsightCardCollapsibleToggleWrapper>
          </InsightCardHeaderRight>
        </InsightCardCollapsibleHeader>
        <InsightCardCollapsibleContent open={open}>{children}</InsightCardCollapsibleContent>
      </InsightCardWrapper>
    </div>
  )
}
